import { FILES_EXPORT_NOTE } from '@ofmos/shared/files';
import type { FilterNode } from '@ofmos/shared/filter-ast';
import type { OrderBy, RecordQuery } from '@ofmos/shared/record-query';
import { hasFlag } from '@ofmos/shared/roles';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { ObjectMetadata } from '../../data/client';
import { useFields } from '../../data/hooks';
import { Button, Checkbox, Dialog, useToast } from '../../ui';
import { useSession } from '../auth/useSession';
import { plural } from './copy';
import { useCreateExport } from './queries';

/**
 * EXPORT WHAT THIS SCREEN IS SHOWING (D32).
 *
 * The request carries a `RecordQuery` built from live state - the visible columns as
 * `select`, the current filter and sort, and the archive segment as `includeDeleted` -
 * rather than a saved-view id. That removes Attio's published caveat structurally: "Save
 * for everyone before exporting" is a workaround for a server that can only see persisted
 * view state, and ours does not have to be. An unsaved filter tweak exports exactly as
 * displayed.
 *
 * "ALL FIELDS" IS A SECOND EXPLICIT CHOICE, DEFAULTING OFF, matching HubSpot's Customize
 * dialog and every other reference: hidden columns are excluded, and a person who wants
 * everything says so.
 *
 * A SELECTION WOULD NARROW IT and there is nothing to narrow with: this product's record
 * list has no multi-select, so `recordIds` has no source in the UI today. The client
 * carries the parameter because the server does; the day a selection exists, this is where
 * it goes, capped at the same `EXPORT_SELECTION_MAX` the server enforces.
 *
 * THE BUTTON IS ABSENT WITHOUT `EXPORT_CSV` (D33). Both routes refuse it - the request and
 * the download, separately - and a control whose only outcome is a refusal is noise on a
 * screen somebody uses all day.
 */
export interface ExportActionProps {
  readonly object: ObjectMetadata;
  /** The field ids the screen is showing, in the order it shows them. */
  readonly columnFieldIds: readonly string[];
  /** D17 already resolved against the reader's clock, exactly as the list queries it. */
  readonly filter: FilterNode | undefined;
  readonly orderBy: readonly OrderBy[] | null;
  readonly archived: boolean;
  readonly block?: boolean;
}

export function ExportAction({
  object,
  columnFieldIds,
  filter,
  orderBy,
  archived,
  block = false,
}: ExportActionProps) {
  const session = useSession();
  const [open, setOpen] = useState(false);

  if (session.data == null || !hasFlag(session.data.permissions, 'EXPORT_CSV')) return null;

  return (
    <>
      <Button
        variant="secondary"
        icon="arrow-down"
        block={block}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        Export
      </Button>
      {open && (
        <ExportDialog
          object={object}
          columnFieldIds={columnFieldIds}
          filter={filter}
          orderBy={orderBy}
          archived={archived}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function ExportDialog({
  object,
  columnFieldIds,
  filter,
  orderBy,
  archived,
  onClose,
}: Omit<ExportActionProps, 'block'> & { readonly onClose: () => void }) {
  const create = useCreateExport();
  const navigate = useNavigate();
  const toast = useToast();
  const [allFields, setAllFields] = useState(false);
  const fields = useFields(object.id);

  /**
   * D12's EXPORT NOTE, AND IT FOLLOWS THE SELECTION (`file-attachments.md` D12, section 12).
   *
   * A Files column carries the file NAMES joined by a semicolon, which is the only part of
   * an attachment that means anything to somebody reading a spreadsheet. Whoever exports
   * expecting the documents themselves has to read that HERE, before the file is written,
   * rather than work it out from a cell full of filenames.
   *
   * Both halves of the selection ask the same question: the checkbox widens it to every
   * field on the object, and without it only the columns on screen are exported.
   */
  const includesFiles = (fields.data ?? []).some(
    (field) =>
      field.type === 'FILES' && !field.isSystem && (allFields || columnFieldIds.includes(field.id)),
  );

  const query: RecordQuery = {
    objectId: object.id,
    select: [...columnFieldIds],
    ...(filter === undefined ? {} : { filter }),
    ...(orderBy === null || orderBy.length === 0 ? {} : { orderBy }),
    /** D32: deleted rows follow the view rather than inventing a third rule. */
    ...(archived ? { includeDeleted: true } : {}),
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`Export ${object.labelPlural}`}
      description="The file is written in the background and appears under Exports when it is ready."
      footer={
        <div className="flex flex-col-reverse gap-2 md:flex-row md:justify-end">
          <Button variant="ghost" className="w-full md:w-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="w-full md:w-auto"
            loading={create.isPending}
            onClick={() =>
              create.mutate(
                { query, allFields },
                {
                  onSuccess: () => {
                    onClose();
                    toast({ title: 'Writing the file. It appears under Exports when it is done.' });
                    void navigate('/settings/exports');
                  },
                  onError: (error: Error) => toast({ tone: 'danger', title: error.message }),
                },
              )
            }
          >
            Export
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-ink-muted">
          {filter === undefined
            ? `Every ${archived ? 'deleted' : 'live'} record in this list, exactly as it is filtered and sorted on screen.`
            : `The records this list is currently filtered down to, in the order it shows them.`}
        </p>
        <Checkbox
          checked={allFields}
          onCheckedChange={setAllFields}
          label="Include every field on this object"
        />
        <p className="text-xs text-ink-muted">
          {allFields
            ? 'Every field, including the ones this screen is not showing.'
            : `The ${plural(columnFieldIds.length, 'column')} this screen is showing. Columns you have hidden are left out.`}
        </p>
        {includesFiles && <p className="text-xs text-ink-muted">{FILES_EXPORT_NOTE}</p>}
        <p className="text-xs text-ink-subtle">
          The file is kept for 7 days and then deleted. It is never emailed and never leaves this
          instance until you download it.
        </p>
      </div>
    </Dialog>
  );
}
